import React, { useState } from 'react';
import {
  Box,
  Card,
  Checkbox,
  IconButton,
  Input,
  Tooltip,
  Typography,
} from '@mui/joy';
import { motion } from 'framer-motion';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import DataObjectIcon from '@mui/icons-material/DataObject';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const MotionCard = motion.create(Card);

/**
 * Get icon for file type based on name/extension
 */
const getFileIcon = (fileName, size = 48) => {
  const ext = fileName.split('.').pop()?.toLowerCase();

  const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];
  const pdfExts = ['pdf'];
  const docExts = ['doc', 'docx', 'txt', 'rtf'];
  const spreadsheetExts = ['xls', 'xlsx', 'csv'];
  const jsonExts = ['json'];

  const iconSx = { fontSize: size };

  if (imageExts.includes(ext)) return <ImageIcon sx={{ ...iconSx, color: 'primary.500' }} />;
  if (pdfExts.includes(ext)) return <PictureAsPdfIcon sx={{ ...iconSx, color: '#e53935' }} />;
  if (docExts.includes(ext)) return <DescriptionIcon sx={{ ...iconSx, color: '#1976d2' }} />;
  if (spreadsheetExts.includes(ext)) return <TableChartIcon sx={{ ...iconSx, color: '#388e3c' }} />;
  if (jsonExts.includes(ext)) return <DataObjectIcon sx={{ ...iconSx, color: '#f57c00' }} />;

  return <InsertDriveFileOutlinedIcon sx={{ ...iconSx, color: 'neutral.500' }} />;
};

const FileCard = ({
  item,
  isSelected,
  onSelect,
  onOpen,
  onContextMenu,
  onRename,
  isRenaming,
  onRenameSubmit,
  onRenameCancel,
  isDragging,
  isDropTarget,
  dragHandleProps,
  draggableProps,
  innerRef,
}) => {
  const [hovered, setHovered] = useState(false);
  const [renameValue, setRenameValue] = useState(item.name);
  const isFolder = item.type === 'folder';

  const handleDoubleClick = (e) => {
    if (!isRenaming) {
      e.stopPropagation();
      onOpen(item);
    }
  };

  const handleClick = (e) => {
    if (!isRenaming) {
      onSelect(item.path, e);
    }
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onRenameSubmit(renameValue);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onRenameCancel();
    }
  };

  const handleMoreClick = (e) => {
    e.stopPropagation();
    onContextMenu(e, item);
  };

  return (
    <MotionCard
      ref={innerRef}
      {...draggableProps}
      {...dragHandleProps}
      variant="outlined"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={(e) => onContextMenu(e, item)}
      initial={false}
      animate={{
        scale: isDragging ? 1.05 : 1,
        boxShadow: isDragging
          ? '0 8px 24px rgba(0,0,0,0.15)'
          : isSelected
          ? '0 0 0 2px var(--joy-palette-primary-500)'
          : 'none',
      }}
      transition={{ duration: 0.15 }}
      sx={{
        cursor: 'pointer',
        userSelect: 'none',
        position: 'relative',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        minHeight: 140,
        bgcolor: isSelected
          ? 'primary.50'
          : isDropTarget
          ? 'primary.100'
          : 'background.surface',
        borderColor: isSelected
          ? 'primary.500'
          : isDropTarget
          ? 'primary.400'
          : 'neutral.200',
        borderWidth: isDropTarget ? 2 : 1,
        borderStyle: isDropTarget ? 'dashed' : 'solid',
        opacity: isDragging ? 0.8 : 1,
        transition: 'background-color 0.15s, border-color 0.15s',
        '&:hover': {
          bgcolor: isSelected ? 'primary.100' : 'background.level1',
          borderColor: isSelected ? 'primary.500' : 'neutral.300',
        },
      }}
    >
      {/* Checkbox - visible on hover or when selected */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          opacity: hovered || isSelected ? 1 : 0,
          transition: 'opacity 0.15s',
        }}
      >
        <Checkbox
          size="sm"
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            onSelect(item.path, { ctrlKey: true });
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </Box>

      {/* More button - visible on hover */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.15s',
        }}
      >
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={handleMoreClick}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Icon */}
      <Box sx={{ mt: 1 }}>
        {isFolder ? (
          <FolderIcon sx={{ fontSize: 48, color: '#ffc107' }} />
        ) : (
          getFileIcon(item.name, 48)
        )}
      </Box>

      {/* Name */}
      {isRenaming ? (
        <Input
          size="sm"
          autoFocus
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={handleRenameKeyDown}
          onBlur={() => onRenameCancel()}
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: '100%',
            textAlign: 'center',
            '--Input-focusedThickness': '1px',
          }}
        />
      ) : (
        <Tooltip title={item.name} placement="bottom" enterDelay={500}>
          <Typography
            level="body-sm"
            fontWeight={500}
            sx={{
              textAlign: 'center',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
            }}
          >
            {item.name}
          </Typography>
        </Tooltip>
      )}
    </MotionCard>
  );
};

export default FileCard;
